import type { Agent, Region } from "@fingerprintjs/fingerprintjs-pro"
import getFingerprintProPublicKey, {
  type FingerprintProPublicKeyData
} from "@/services/getFingerprintProPublicKey"
import fingerprintProTracker from "@/services/fingerprintProTracker"

const DEFAULT_FINGERPRINT_PRO_REGION: Region = "ap"

const buildFppAlertPrefix = (linkedId: string) => `[Alert][${linkedId}]`
const buildFppRateLimitMsg = (linkedId: string) =>
  `${buildFppAlertPrefix(linkedId)} Public Key: too many requests.`

// Raw error string emitted by FingerprintJS Pro SDK when burst rate limit is
// hit; matched against to translate into the TG-friendly rate-limit message.
const SDK_RATE_LIMIT_SIGNATURE = "Too many requests, rate limit exceeded"

const isFingerprintProRegion = (value: string): value is Region =>
  value === "us" || value === "eu" || value === "ap"

const resolveRegion = (): Region => {
  const envRegion = import.meta.env.VITE_FINGERPRINT_PRO_REGION
  return envRegion && isFingerprintProRegion(envRegion)
    ? envRegion
    : DEFAULT_FINGERPRINT_PRO_REGION
}

const resolvePublicKeyData = async (): Promise<
  FingerprintProPublicKeyData | undefined
> => {
  try {
    return await getFingerprintProPublicKey.handler()
  } catch (error) {
    console.warn("getFingerprintProPublicKey failed", error)
    return undefined
  }
}

let cachedAgent: Agent | undefined

const initAgent = async (apiKey: string): Promise<Agent> => {
  if (cachedAgent) return cachedAgent

  const { load } = await import("@fingerprintjs/fingerprintjs-pro")
  cachedAgent = await load({ apiKey, region: resolveRegion() })

  return cachedAgent
}

const extractRawErrorMsg = (error: unknown): string => {
  if (error instanceof Error) return error.message
  if (typeof error === "string") return error

  try {
    return JSON.stringify(error)
  } catch {
    return "Unknown FingerprintJS Pro error"
  }
}

// Translate the SDK rate-limit signature into the SACRIC-1187 TG wording, and
// forward every other raw error verbatim with the alert prefix so unexpected
// signals (e.g. "Subscription restricted") reach TG intact instead of being
// silently re-labelled.
const formatTrackerErrorMsg = (rawErrorMsg: string, linkedId: string): string => {
  if (rawErrorMsg.includes(SDK_RATE_LIMIT_SIGNATURE))
    return buildFppRateLimitMsg(linkedId)
  return `${buildFppAlertPrefix(linkedId)} ${rawErrorMsg}`
}

interface FingerprintProSDKResult {
  eventId?: string
  errorMsg?: string
}

const runSDK = async (
  apiKey: string,
  linkedId: string
): Promise<FingerprintProSDKResult> => {
  try {
    const agent = await initAgent(apiKey)
    const result = await agent.get({ linkedId, tag: { data: "test" } })

    return { eventId: result.requestId }
  } catch (error) {
    console.warn("FingerprintJS Pro SDK failed", error)

    return {
      errorMsg: formatTrackerErrorMsg(extractRawErrorMsg(error), linkedId)
    }
  }
}

export const runFingerprintProFlow = async () => {
  const publicKeyData = await resolvePublicKeyData()
  if (!publicKeyData) return undefined

  const { publicKey, siteId, createdDate, userId } = publicKeyData
  const linkedId = `${siteId}_${userId}_${createdDate}`
  const { eventId, errorMsg } = await runSDK(publicKey, linkedId)

  return fingerprintProTracker.handler(
    eventId
      ? { eventId, publicKey }
      : {
          errorMsg:
            errorMsg ?? `${buildFppAlertPrefix(linkedId)} Unknown FingerprintJS Pro error`
        }
  )
}

export default runFingerprintProFlow
